import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Mail as MailIcon, Building2, User, Mail, Briefcase } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { INDUSTRIES, COMPANY_SIZES, isOfficialEmail } from "@/data/taxonomy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = {
  full_name: "",
  email: "",
  linkedin: "",
  company: "",
  designation: "",
  industry: "",
  company_size: "",
};

export default function SignupDialog({ open, onOpenChange }) {
  const navigate = useNavigate();  // eslint-disable-line no-unused-vars
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [error, setError] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your name.";
    if (!form.email.trim()) return "Please enter your work email.";
    if (!isOfficialEmail(form.email))
      return "Please use your official work email — free providers (gmail, yahoo, etc.) aren't accepted. LinkedIn Company URL also works.";
    if (!form.company.trim()) return "Please enter your company name.";
    if (!form.designation.trim()) return "Please enter your designation.";
    if (!form.industry) return "Please pick your industry.";
    if (!form.company_size) return "Please pick your company size.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/signup`, {
        ...form,
        email: form.email.trim().toLowerCase(),
      });
      // Persist a lightweight session
      localStorage.setItem(
        "deployd_member",
        JSON.stringify({
          email: data.email,
          industry: data.industry,
          full_name: form.full_name.trim(),
          company: form.company.trim(),
        })
      );
      toast.success("You're on the list.");
      setSubmittedEmail(data.email || form.email.trim().toLowerCase());
      setSubmitted(true);
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d).join(" · ")
        : typeof detail === "string"
        ? detail
        : "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (o) => {
    if (submitting) return;
    if (!o) setSubmitted(false);
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="signup-dialog"
        className="max-h-[92vh] overflow-y-auto border border-zinc-300 bg-white p-0 sm:max-w-xl"
      >
        {submitted ? (
          <div data-testid="signup-thanks" className="p-8 sm:p-10 text-center">
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full border-2 border-emerald-500 bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <p className="mt-6 font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-700">
              // you&apos;re on the list
            </p>
            <h3 className="mt-3 font-display text-2xl font-extrabold leading-snug tracking-tight text-zinc-900 sm:text-3xl">
              Thanks for signing up.
            </h3>
            <p className="mt-4 text-base leading-relaxed text-zinc-700 sm:text-lg">
              You&apos;ll get the{" "}
              <span className="font-semibold text-zinc-900">first verified use case</span>{" "}
              in your inbox shortly.
            </p>
            {submittedEmail && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-medium text-zinc-600">
                <MailIcon className="h-3.5 w-3.5 text-emerald-600" />
                {submittedEmail}
              </p>
            )}
            <button
              type="button"
              data-testid="signup-thanks-close"
              onClick={() => handleOpenChange(false)}
              className="mt-8 inline-flex items-center gap-2 border border-zinc-900 bg-zinc-900 px-6 py-3 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600"
            >
              Done
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
        <div className="p-6 sm:p-8">
          <DialogHeader>
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-700">
              // count me in
            </p>
            <DialogTitle className="mt-3 font-display text-2xl font-extrabold leading-snug tracking-tight text-zinc-900 sm:text-3xl">
              Tell us about you.
            </DialogTitle>
            <DialogDescription className="font-mono-tech text-[11px] uppercase tracking-[0.24em] text-zinc-500">
              Sign up and we will mail you a curated set of what tools are being used by whom
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              testId="signup-name"
              icon={<User className="h-3.5 w-3.5" />}
              label="Full name"
              value={form.full_name}
              onChange={upd("full_name")}
              placeholder="Jane Doe"
              className="sm:col-span-2"
            />
            <Field
              testId="signup-email"
              icon={<Mail className="h-3.5 w-3.5" />}
              label="Work email"
              value={form.email}
              onChange={upd("email")}
              placeholder="you@company.com"
              type="email"
              className="sm:col-span-2"
            />
            <Field
              testId="signup-linkedin"
              icon={<Briefcase className="h-3.5 w-3.5" />}
              label="LinkedIn URL (optional)"
              value={form.linkedin}
              onChange={upd("linkedin")}
              placeholder="linkedin.com/in/you"
              required={false}
              className="sm:col-span-2"
            />
            <Field
              testId="signup-company"
              icon={<Building2 className="h-3.5 w-3.5" />}
              label="Company"
              value={form.company}
              onChange={upd("company")}
              placeholder="Company, Inc."
            />
            <Field
              testId="signup-designation"
              icon={<Briefcase className="h-3.5 w-3.5" />}
              label="Designation"
              value={form.designation}
              onChange={upd("designation")}
              placeholder="Head of Engineering"
            />
            <Select
              testId="signup-industry"
              label="Industry"
              value={form.industry}
              onChange={upd("industry")}
              options={INDUSTRIES}
              placeholder="Select industry"
            />
            <Select
              testId="signup-size"
              label="Company size"
              value={form.company_size}
              onChange={upd("company_size")}
              options={COMPANY_SIZES}
              placeholder="Select size"
            />

            {error && (
              <p
                data-testid="signup-error"
                className="border border-red-300 bg-red-50 px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-red-700 sm:col-span-2"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="signup-submit"
              disabled={submitting}
              className="group mt-2 inline-flex items-center justify-between border border-zinc-900 bg-zinc-900 px-5 py-3.5 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-white transition-colors hover:border-emerald-600 hover:bg-emerald-600 disabled:opacity-60 sm:col-span-2"
            >
              <span>{submitting ? "Signing you in…" : "Count me in"}</span>
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              )}
            </button>
          </form>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ testId, label, icon, className = "", required = true, ...rest }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
      {icon}
      {label}
    </label>
    <input
      data-testid={testId}
      required={required}
      {...rest}
      className="w-full border border-zinc-300 bg-white px-3 py-2.5 font-display text-base text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-emerald-500"
    />
  </div>
);

const Select = ({ testId, label, value, onChange, options, placeholder }) => (
  <div className="flex flex-col gap-1.5">
    <label className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
      {label}
    </label>
    <select
      data-testid={testId}
      required
      value={value}
      onChange={onChange}
      className="w-full border border-zinc-300 bg-white px-3 py-2.5 font-display text-base text-zinc-900 outline-none transition-colors focus:border-emerald-500"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);
